// Update Feed store — reactive state for the web Update Feed (ADR-0007).
// Holds scope, current page, loading/error, and the fetched Binder Updates.
// Orchestration only; presentation math lives in logic/updates-logic.js and
// fetching in services/updates-api.js.

import { fetchUpdateFeed, fetchUpdateDetail } from '../services/updates-api.js';

const PAGE_SIZE = 20;

export const updatesState = $state({
  /** @type {'group'|'binder'|null} */
  scope: null,
  /** @type {string|null} */
  groupId: null,
  /** @type {string|null} short URL, e.g. '/mfb.abc' */
  binderUrl: null,
  /** @type {string[]} the group scope's subgroup selection; [] = whole server */
  subgroupIds: [],
  /** @type {number} 1-indexed page (newest first) */
  page: 1,
  /** @type {number} */
  pageSize: PAGE_SIZE,
  /** @type {number} */
  total: 0,
  /** @type {number} */
  totalPages: 0,
  /** @type {Array} the current page's Binder Updates */
  updates: [],
  /** @type {boolean} */
  loading: false,
  /** @type {string|null} */
  error: null,

  // Detail view state (the /{short}/updates/{seq} permalink)
  /** @type {Object|null} */
  detail: null,
  /** @type {boolean} */
  detailLoading: false,
  /** @type {string|null} */
  detailError: null,
});

/**
 * Set the feed scope and reset to the newest page. Does not fetch — call
 * loadFeed() after. A scope ('group' | 'binder') is required — there is no
 * "my feed".
 * The group scope takes the picker's subgroup selection ([] = whole server).
 * @param {{ scope: 'group'|'binder', groupId?: string|null, binderUrl?: string|null,
 *           subgroupIds?: string[] }} opts
 */
export function setScope({ scope, groupId = null, binderUrl = null, subgroupIds = [] }) {
  updatesState.scope = scope;
  updatesState.groupId = groupId;
  updatesState.binderUrl = binderUrl;
  updatesState.subgroupIds = scope === 'group' ? [...subgroupIds] : [];
  updatesState.page = 1;
}

/**
 * Load the current page of the feed for the current scope.
 * @returns {Promise<void>}
 */
export async function loadFeed() {
  updatesState.loading = true;
  updatesState.error = null;
  try {
    const result = await fetchUpdateFeed({
      scope: updatesState.scope,
      groupId: updatesState.groupId,
      binderUrl: updatesState.binderUrl,
      subgroupIds: [...updatesState.subgroupIds],
      page: updatesState.page,
      pageSize: updatesState.pageSize,
    });
    updatesState.updates = result.updates;
    updatesState.total = result.total;
    updatesState.totalPages = result.totalPages;
    if (typeof result.page === 'number') updatesState.page = result.page;
    console.log(
      `[Updates] Loaded feed scope=${updatesState.scope} page=${updatesState.page}: ` +
        `${result.updates.length} updates (${result.total} total)`
    );
  } catch (e) {
    console.error('[Updates] Failed to load feed:', e);
    updatesState.error = e.message || 'Failed to load updates';
    updatesState.updates = [];
  } finally {
    updatesState.loading = false;
  }
}

/**
 * Go to a 1-indexed page and reload.
 * @param {number} page
 */
export async function goToPage(page) {
  updatesState.page = Math.max(1, page);
  await loadFeed();
}

/**
 * Load a single Binder Update's detail (the /{short}/updates/{seq} permalink).
 * @param {string} binderUrl - short URL (e.g. '/mfb.abc')
 * @param {string|number} seq - per-binder sequence number
 */
export async function loadDetail(binderUrl, seq) {
  updatesState.detailLoading = true;
  updatesState.detailError = null;
  updatesState.detail = null;
  try {
    const result = await fetchUpdateDetail(binderUrl, seq);
    updatesState.detail = result;
  } catch (e) {
    console.error('[Updates] Failed to load update detail:', e);
    updatesState.detailError = e.message || 'Failed to load update';
  } finally {
    updatesState.detailLoading = false;
  }
}
